import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { isRoad } from '../../utils/partnerTypes';
import { useApp } from '../../context/AppContext';
import { useCart } from '../../context/CartContext';
import { Reveal, stagger } from '../../components/Motion';
import {
  CategoryTile, PartnerCard, ServiceCard, ProductCard, JobCard, VehicleCard,
} from '../../components/Cards';
import { SectionHeader, Empty, Skeleton, Button } from '../../components/Ui';
import { VERTICALS } from '../../constants';
import { colors } from '../../theme';

const QUICK = [
  { icon: '🚕', label: 'Book a ride', to: '/ride' },
  { icon: '📦', label: 'Send parcel', to: '/ride?type=courier' },
  { icon: '🏠', label: 'Property', to: '/property' },
  { icon: '🐄', label: 'Animals', to: '/animals' },
  { icon: '🎂', label: 'Special days', to: '/special-days' },
  { icon: '🏨', label: 'Hotels', to: '/hotels' },
  { icon: '🏍️', label: 'Bike service', to: '/bike-service' },
  { icon: '🚗', label: 'Car service', to: '/bike-service?family=car' },
  { icon: '💼', label: 'Find jobs', to: '/jobs' },
  { icon: '✨', label: 'Your Desire', to: '/desire' },
];

// Ride, cab and parcel partners never hire, so they are not offered the list.
const HIRE_QUICK = { icon: '🧑‍🔧', label: 'Hire people', to: '/people' };

/**
 * Home feed.
 *
 * The vertical switcher at the top is the "multi service" selector — picking a
 * vertical re-themes the strip and filters the category grid to that group, so
 * a customer can jump straight into Food, Home Services, Auto etc.
 */
const HomePage = () => {
  const navigate = useNavigate();
  const { user, partner } = useAuth();
  const quick = isRoad(partner) ? QUICK : [...QUICK, HIRE_QUICK];
  const { location, vertical, setVertical, refreshUnread } = useApp();
  const { addItem, quantityOf } = useCart();

  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bannerIndex, setBannerIndex] = useState(0);
  const bannerRef = useRef(null);

  const active = VERTICALS.find((v) => v.key === vertical) || VERTICALS[0];

  const load = useCallback(async () => {
    try {
      const res = await api.app.home({ lat: location.latitude, lng: location.longitude });
      setFeed(res.data);
    } catch (e) {
      setFeed(null);
    } finally {
      setLoading(false);
    }
  }, [location.latitude, location.longitude]);

  useEffect(() => { load(); refreshUnread(); }, [load, refreshUnread]);

  // Auto-advance the banner carousel.
  useEffect(() => {
    const banners = (feed && feed.banners) || [];
    if (banners.length < 2) return undefined;
    const timer = setInterval(() => {
      setBannerIndex((i) => {
        const next = (i + 1) % banners.length;
        const rail = bannerRef.current;
        if (rail) rail.scrollTo({ left: next * rail.clientWidth, behavior: 'smooth' });
        return next;
      });
    }, 4500);
    return () => clearInterval(timer);
  }, [feed]);

  const categories = ((feed && feed.categories) || [])
    .filter((c) => !active.group || c.group === active.group);

  if (loading) {
    return (
      <div className="col" style={{ gap: 14 }}>
        <Skeleton height={54} />
        <Skeleton height={170} />
        <div className="grid grid-cat">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <Skeleton key={i} height={86} />)}
        </div>
        <Skeleton height={120} />
        <Skeleton height={120} />
      </div>
    );
  }

  if (!feed) {
    return (
      <Empty
        emoji="📡"
        title="Cannot reach KAAMWALA"
        text="Check that the server is running, then retry."
        actionLabel="Retry"
        onAction={() => { setLoading(true); load(); }}
      />
    );
  }

  return (
    <div>
      {/* Vertical switcher */}
      <div
        className="row no-bar"
        style={{
          gap: 8, overflowX: 'auto', padding: '2px 0 12px',
          position: 'sticky', top: 0, background: colors.bg, zIndex: 5,
        }}
      >
        {VERTICALS.map((v) => {
          const isActive = v.key === vertical;
          return (
            <button
              key={v.key}
              type="button"
              onClick={() => setVertical(v.key)}
              className="press"
              style={{
                flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 6,
                padding: '9px 14px', borderRadius: 999, fontSize: 13,
                fontWeight: isActive ? 800 : 600,
                border: `1px solid ${isActive ? v.color : colors.line}`,
                background: isActive ? v.color : '#fff',
                color: isActive ? '#fff' : colors.ink2,
                transition: 'background .18s ease, color .18s ease, border-color .18s ease',
              }}
            >
              <span>{v.icon}</span>
              {v.label}
            </button>
          );
        })}
      </div>

      <Reveal>
        <h1 className="h2">Hi {(user && user.name ? user.name.split(' ')[0] : 'there')} 👋</h1>
        <div className="t-small" style={{ marginTop: 2 }}>What do you need done today?</div>
      </Reveal>

      {/* Banners */}
      {feed.banners && feed.banners.length ? (
        <Reveal delay={80} style={{ marginTop: 16 }}>
          <div
            ref={bannerRef}
            className="rail no-bar"
            style={{ scrollSnapType: 'x mandatory' }}
            onScroll={(e) => {
              const w = e.currentTarget.clientWidth || 1;
              setBannerIndex(Math.round(e.currentTarget.scrollLeft / w));
            }}
          >
            {feed.banners.map((b) => (
              <div
                key={b._id}
                style={{
                  width: '100%', height: 172, borderRadius: 16, overflow: 'hidden',
                  position: 'relative', background: colors.primaryLight,
                }}
              >
                <img
                  src={fileUrl(b.image)}
                  alt={b.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <div
                  style={{
                    position: 'absolute', inset: 'auto 0 0 0', padding: 14,
                    background: 'linear-gradient(transparent, rgba(11,31,80,.78))', color: '#fff',
                  }}
                >
                  <div style={{ fontSize: 16, fontWeight: 800 }}>{b.title}</div>
                  {b.subtitle ? <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>{b.subtitle}</div> : null}
                </div>
              </div>
            ))}
          </div>
          {feed.banners.length > 1 ? (
            <div className="row" style={{ justifyContent: 'center', gap: 5, marginTop: 8 }}>
              {feed.banners.map((b, i) => (
                <span
                  key={b._id}
                  style={{
                    width: i === bannerIndex ? 18 : 6, height: 6, borderRadius: 3,
                    background: i === bannerIndex ? colors.primary : colors.line,
                    transition: 'width .25s ease, background .25s ease',
                  }}
                />
              ))}
            </div>
          ) : null}
        </Reveal>
      ) : null}

      {/* Category grid for the selected vertical */}
      <SectionHeader
        title={active.key === 'all' ? 'All services' : active.label}
        subtitle={`${categories.length} categories`}
        actionLabel="See all"
        onAction={() => navigate('/services')}
      />
      <div className="grid grid-cat">
        {categories.slice(0, 16).map((c, i) => (
          <Reveal key={c._id} delay={stagger(i, 28)}>
            <CategoryTile category={c} onClick={() => navigate(`/category/${c._id}`, { state: { category: c } })} />
          </Reveal>
        ))}
        {categories.length > 16 ? (
          <button
            type="button"
            onClick={() => navigate('/services')}
            className="press"
            style={{
              background: 'none', border: 0, display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 6,
            }}
          >
            <span
              style={{
                width: 56, height: 56, borderRadius: 16, background: colors.primaryLight,
                display: 'grid', placeItems: 'center', color: colors.primary, fontWeight: 800, fontSize: 18,
              }}
            >
              +{categories.length - 16}
            </span>
            <span className="t-tiny" style={{ color: colors.ink2 }}>More</span>
          </button>
        ) : null}
      </div>

      {/* Quick actions */}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginTop: 22 }}>
        {quick.map((q, i) => (
          <Reveal key={q.to} delay={i * 50}>
            <button
              type="button"
              onClick={() => navigate(q.to)}
              className="card sh-sm press hoverable"
              style={{
                width: '100%', display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: 6, padding: 14,
              }}
            >
              <span style={{ fontSize: 22 }}>{q.icon}</span>
              <span className="t-tiny" style={{ color: colors.ink2, fontWeight: 700 }}>{q.label}</span>
            </button>
          </Reveal>
        ))}
      </div>

      {/* Nearby businesses */}
      {feed.nearby && feed.nearby.length ? (
        <>
          <SectionHeader
            title="Near you"
            subtitle={`Trusted partners around ${location.city}`}
            actionLabel="See all"
            onAction={() => navigate('/services')}
          />
          <div className="grid grid-cards">
            {feed.nearby.slice(0, 6).map((p, i) => (
              <Reveal key={p._id} delay={stagger(i, 55)}>
                <PartnerCard partner={p} onClick={() => navigate(`/business/${p._id}`)} />
              </Reveal>
            ))}
          </div>
        </>
      ) : null}

      {/* Popular services */}
      {feed.popularServices && feed.popularServices.length ? (
        <>
          <SectionHeader title="Popular services" subtitle="Booked most often near you" />
          <div className="grid grid-list">
            {feed.popularServices.slice(0, 6).map((s, i) => (
              <Reveal key={s._id} delay={stagger(i, 55)}>
                <ServiceCard service={s} onClick={() => navigate(`/service/${s._id}`)} />
              </Reveal>
            ))}
          </div>
        </>
      ) : null}

      {/* Trending products */}
      {feed.trendingProducts && feed.trendingProducts.length ? (
        <>
          <SectionHeader title="Daily essentials" subtitle="From shops around you" />
          <div className="rail">
            {feed.trendingProducts.map((p) => (
              <ProductCard
                key={p._id}
                product={p}
                width={168}
                inCart={quantityOf(p._id)}
                onAdd={() => addItem(p._id, 1).catch(() => {})}
                onClick={() => navigate(`/product/${p._id}`)}
              />
            ))}
          </div>
        </>
      ) : null}

      {/* Jobs */}
      {feed.latestJobs && feed.latestJobs.length ? (
        <>
          <SectionHeader
            title="Jobs near you"
            subtitle="Apply in a tap"
            actionLabel="All jobs"
            onAction={() => navigate('/jobs')}
          />
          <div className="grid grid-cards">
            {feed.latestJobs.slice(0, 4).map((j, i) => (
              <Reveal key={j._id} delay={stagger(i, 55)}>
                <JobCard job={j} onClick={() => navigate(`/jobs/${j._id}`)} />
              </Reveal>
            ))}
          </div>
        </>
      ) : null}

      {/* Vehicles */}
      {feed.latestVehicles && feed.latestVehicles.length ? (
        <>
          <SectionHeader
            title="Vehicles for sale"
            actionLabel="See all"
            onAction={() => navigate('/vehicles')}
          />
          <div className="rail">
            {feed.latestVehicles.map((v) => (
              <VehicleCard
                key={v._id}
                vehicle={v}
                width={230}
                onClick={() => navigate(`/vehicles/${v._id}`)}
              />
            ))}
          </div>
        </>
      ) : null}

      {/* Become a partner */}
      <Reveal delay={120} style={{ marginTop: 26 }}>
        <div
          className="row-between wrap"
          style={{
            background: colors.primary, borderRadius: 22, padding: 22,
            color: '#fff', gap: 16, boxShadow: 'var(--sh-md)',
          }}
        >
          <div style={{ maxWidth: 460 }}>
            <div style={{ fontSize: 19, fontWeight: 800 }}>Sell on KAAMWALA</div>
            <div style={{ fontSize: 13, opacity: 0.88, marginTop: 5, lineHeight: 1.6 }}>
              Own a shop or a skill? List your business free and get customers from your own area.
            </div>
            <Button
              title="Register now →"
              variant="accent"
              size="sm"
              style={{ marginTop: 14 }}
              onClick={() => navigate('/sell')}
            />
          </div>
          <span style={{ fontSize: 52 }}>🏪</span>
        </div>
      </Reveal>
    </div>
  );
};

export default HomePage;
